import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import LinkButton from '@atlaskit/button/link';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { SearchIllustration } from '../assets/SearchIllustration.tsx';
import type {
	ConnectorPromoConfig,
	RecommendedConnector,
} from '../connector-promo-config-types.ts';
import { useAdminHubConnectorLink } from '../hooks/useAdminHubConnectorLink.ts';
import { normalizeFields } from '../utils/analytics.ts';

import { ConnectorIconPlaceholder } from './ConnectorIconPlaceholder.tsx';

const messages = defineMessages({
	heading: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.nthPageCard.heading',
		defaultMessage: 'Your team’s search results could be better',
		description: 'Heading for the connector promo card on the full search page',
	},
	body: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.nthPageCard.body',
		defaultMessage:
			'Connect your team’s most used apps to get better answers and more results across Search and Chat.',
		description: 'Body text for the connector promo card on the full search page',
	},
	allApps: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.nthPageCard.allApps',
		defaultMessage: 'All apps',
		description: 'Link text to view all available apps',
	},
	dismissLabel: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.nthPageCard.dismissLabel',
		defaultMessage: 'Dismiss',
		description: 'Accessibility label for dismiss button',
	},
});

type ConnectorNthPageCardProps = Pick<
	ConnectorPromoConfig,
	'recommendedConnectors' | 'onSetupConnector'
> & {
	onDismissPromo: () => void;
	testId?: string;
};

const styles = cssMap({
	container: {
		display: 'flex',
		alignItems: 'stretch',
		backgroundColor: token('color.background.information'),
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		position: 'relative',
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		paddingInline: token('space.200'),
		paddingBlock: token('space.250'),
		flex: 1,
	},
	textGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
		marginBottom: token('space.200'),
	},
	dismiss: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
	},
	illustration: {
		display: 'flex',
		alignItems: 'flex-end',
		alignSelf: 'flex-end',
		flexShrink: 0,
	},
});

const ConnectorButton = ({
	connector,
	onSetup,
	testId,
}: {
	connector: RecommendedConnector;
	onSetup: (key: string) => void;
	testId: string;
}) => (
	<Button
		appearance="default"
		iconBefore={() => <ConnectorIconPlaceholder connector={connector} size={16} />}
		onClick={() => onSetup(connector.key)}
		testId={testId}
	>
		{connector.displayName}
	</Button>
);

export const ConnectorNthPageCard = ({
	recommendedConnectors,
	onSetupConnector,
	onDismissPromo,
	testId = 'connector-nth-page-card',
}: ConnectorNthPageCardProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const adminHubLink = useAdminHubConnectorLink();
	const { createEvent } = useConfluenceAnalyticsEvents();

	const handleSetupClick = useCallback(
		(key: string) => {
			createEvent({
				type: 'sendUIEvent',
				data: {
					source: 'ConnectorNthPageCard',
					action: 'clicked',
					actionSubject: 'button',
					attributes: normalizeFields({
						connectorKey: key,
						container: 'ConnectorPromoSearchPage',
						testId: `${testId}--connector-${key}`,
					}),
				},
			}).fire();
			onSetupConnector(key);
		},
		[onSetupConnector, createEvent, testId],
	);

	const handleDismiss = useCallback(() => {
		createEvent({
			type: 'sendUIEvent',
			data: {
				source: 'ConnectorNthPageCard',
				action: 'clicked',
				actionSubject: 'button',
				attributes: {
					container: 'ConnectorPromoSearchPage',
					testId: `${testId}--dismiss`,
				},
			},
		}).fire();
		onDismissPromo();
	}, [onDismissPromo, createEvent, testId]);

	if (recommendedConnectors.length === 0) {
		return null;
	}

	return (
		<Flex xcss={styles.container} testId={testId}>
			<Stack xcss={styles.content}>
				<Stack xcss={styles.textGroup}>
					<Text size="large" weight="bold">
						{formatMessage(messages.heading)}
					</Text>
					<Text color="color.text.subtle">{formatMessage(messages.body)}</Text>
				</Stack>
				<Inline space="space.075" alignBlock="center" shouldWrap>
					{recommendedConnectors.slice(0, 3).map((connector) => (
						<ConnectorButton
							key={connector.key}
							connector={connector}
							onSetup={handleSetupClick}
							testId={`${testId}--connector-${connector.key}`}
						/>
					))}
					<LinkButton
						href={adminHubLink ?? '/admin'}
						appearance="subtle"
						iconAfter={LinkExternalIcon}
						testId={`${testId}--all-apps`}
					>
						{formatMessage(messages.allApps)}
					</LinkButton>
				</Inline>
			</Stack>
			<Box xcss={styles.illustration}>
				<SearchIllustration width={232} height={136} viewBox="0 0 232 130" />
			</Box>
			<Box xcss={styles.dismiss}>
				<IconButton
					appearance="subtle"
					icon={CrossIcon}
					label={formatMessage(messages.dismissLabel)}
					onClick={handleDismiss}
					testId={`${testId}--dismiss`}
				/>
			</Box>
		</Flex>
	);
};
